import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon, FileTextIcon, EnvelopeIcon, ArrowRightIcon, CheckCircleIcon, BedIcon, ConfettiIcon, StarIcon, DownloadSimpleIcon, WhatsappLogoIcon, PaperPlaneTiltIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const EMPTY_ROW = { category: "room", description: "", qty: 1, rate: 0, plan_code: "", plate_type: "" };
const EMPTY_FORM = {
  customer_name: "", customer_phone: "", customer_email: "", purpose: "",
  valid_till: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
  tax_rate: 18, tax_inclusive: true, discount: 0, notes: "",
  line_items: [{ ...EMPTY_ROW }],
};

const statusColor = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-primary/15 text-primary",
  accepted: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  rejected: "bg-destructive/15 text-destructive",
  converted: "bg-secondary/15 text-secondary",
};

export default function Quotations() {
  const { activeId, active } = useProperty();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState({ meal_plans: [], extra_bed_rate: 500, cnb_rate: 300 });

  const load = () => {
    if (!activeId) return;
    api.get("/quotations", { params: { property_id: activeId } }).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [activeId]);
  useEffect(() => { api.get("/settings/platform").then((r) => setPlatform(r.data || {})).catch(() => {}); }, []);

  const addRow = () => setForm({ ...form, line_items: [...form.line_items, { ...EMPTY_ROW }] });
  const patchRow = (idx, patch) => setForm({ ...form, line_items: form.line_items.map((r, i) => i === idx ? { ...r, ...patch } : r) });
  const removeRow = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  // Prefill row description when category / plan is picked
  const applyCategoryDefaults = (idx, cat) => {
    const li = form.line_items[idx];
    const patch = { category: cat };
    if (cat === "room" && !li.description) patch.description = "Deluxe room · per night";
    if (cat === "hall") { patch.description = li.description || "Banquet hall · per plate"; patch.plate_type = li.plate_type || "veg"; }
    if (cat === "amenity" && !li.description) patch.description = "Spa · session";
    patchRow(idx, patch);
  };
  const applyPlan = (idx, plan_code) => {
    const li = form.line_items[idx];
    const plan = (platform.meal_plans || []).find((p) => p.code === plan_code);
    const patch = { plan_code };
    if (plan) {
      const currentRate = Number(li.rate) || 0;
      const surcharge = Number(plan.surcharge_per_person || 0);
      if (surcharge > 0 && currentRate <= 0) patch.rate = surcharge;
      patch.description = `${li.description || "Room"} · ${plan.code} plan`;
    }
    patchRow(idx, patch);
  };

  const totals = useMemo(() => {
    const subtotal = form.line_items.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.rate) || 0), 0);
    const discount = Number(form.discount) || 0;
    const gross = Math.max(0, subtotal - discount);
    const rate = Number(form.tax_rate) || 0;
    let tax = 0; let total = gross;
    if (form.tax_inclusive) {
      const taxable = rate ? gross / (1 + rate / 100) : gross;
      tax = gross - taxable;
    } else {
      tax = gross * rate / 100;
      total = gross + tax;
    }
    return { subtotal, discount, tax, total };
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/quotations", {
        ...form,
        property_id: activeId,
        line_items: form.line_items.filter((r) => r.description).map((r) => ({
          description: r.description, qty: Number(r.qty), rate: Number(r.rate),
          category: r.category, plan_code: r.plan_code || undefined, plate_type: r.plate_type || undefined,
        })),
        discount: Number(form.discount) || 0,
        tax_rate: Number(form.tax_rate) || 0,
      });
      toast.success("Quotation created");
      setOpen(false); setForm(EMPTY_FORM); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/quotations/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this quotation?")) return;
    await api.delete(`/quotations/${id}`);
    toast.success("Deleted"); load();
  };

  const downloadPdf = (id, number) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/quotations/${id}/pdf`;
    // Open in new tab with token via cookie/header — use fetch + blob to force download
    api.get(`/quotations/${id}/pdf`, { responseType: "blob" }).then((res) => {
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.target = "_blank";
      link.download = `${number || "quotation"}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 4000);
    }).catch(() => toast.error("PDF download failed"));
  };

  const [shareTarget, setShareTarget] = useState(null);
  const [shareChannel, setShareChannel] = useState("email");
  const [shareTo, setShareTo] = useState("");
  const [shareMsg, setShareMsg] = useState("");
  const [shareBusy, setShareBusy] = useState(false);

  const openShare = (r, channel) => {
    setShareTarget(r);
    setShareChannel(channel);
    setShareTo(channel === "email" ? (r.customer_email || "") : (r.customer_phone || ""));
    setShareMsg("");
  };
  const sendShare = async () => {
    if (!shareTarget) return;
    if (!shareTo.trim()) return toast.error(`Enter a ${shareChannel === "email" ? "email address" : "phone number"}`);
    setShareBusy(true);
    try {
      const r = await api.post(`/quotations/${shareTarget.id}/send`, { channel: shareChannel, to: shareTo, message: shareMsg });
      const via = shareChannel === "email" ? "emailed" : "sent via WhatsApp";
      toast.success(`Quotation ${via} to ${r.data.to}${r.data.provider === "email_stub" || r.data.provider === "whatsapp_stub" ? " (delivery is stubbed pending real integration)" : ""}`);
      setShareTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setShareBusy(false); }
  };

  const filtered = q.trim()
    ? items.filter((r) => (r.number + " " + r.customer_name + " " + (r.purpose || "")).toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <div>
      <PageHeader
        overline={active ? active.name : "Property"}
        title="Quotations"
        description="Send professional quotes to prospective guests, corporate clients and event enquiries. Convert to a booking once accepted."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="quotations-search" />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="quotations-new-btn"><PlusIcon size={14} className="mr-1" /> New quotation</Button></DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>New quotation</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Customer name *</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required data-testid="quotation-customer-name" /></div>
                    <div><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Wedding reception, Corporate offsite…" data-testid="quotation-purpose" /></div>
                    <div><Label>Phone</Label><Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
                    <div><Label>Email</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
                    <div><Label>Valid till</Label><Input type="date" value={form.valid_till} onChange={(e) => setForm({ ...form, valid_till: e.target.value })} data-testid="quotation-valid-till" /></div>
                    <div><Label>GST rate (%)</Label><Input type="number" step="0.5" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.tax_inclusive} onChange={(e) => setForm({ ...form, tax_inclusive: e.target.checked })} className="accent-primary" data-testid="quotation-tax-inclusive" />
                    Prices are inclusive of GST
                  </label>

                  <div className="rounded-md border border-border p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Line items</p>
                      <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="quotation-add-row"><PlusIcon size={10} className="mr-1" /> Add row</Button>
                    </div>
                    {form.line_items.map((li, idx) => (
                      <div key={idx} className="rounded-md border border-border/60 p-2 space-y-2" data-testid={`quotation-row-${idx}`}>
                        <div className="flex flex-wrap gap-2 items-center">
                          <div className="flex items-center gap-1 rounded-md border border-border overflow-hidden text-xs">
                            {[["room","Room",BedIcon],["hall","Hall/Event",ConfettiIcon],["amenity","Amenity",StarIcon],["other","Other",FileTextIcon]].map(([val,label,Icon]) => (
                              <button
                                type="button" key={val}
                                onClick={() => applyCategoryDefaults(idx, val)}
                                className={`flex items-center gap-1 px-2 py-1 ${li.category === val ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                                data-testid={`quotation-cat-${idx}-${val}`}
                              >
                                <Icon size={11} /> {label}
                              </button>
                            ))}
                          </div>
                          {li.category === "room" && (
                            <Select value={li.plan_code || ""} onValueChange={(v) => applyPlan(idx, v)}>
                              <SelectTrigger className="w-40 h-8 text-xs" data-testid={`quotation-plan-${idx}`}><SelectValue placeholder="Meal plan" /></SelectTrigger>
                              <SelectContent>
                                {(platform.meal_plans || []).map((p) => (
                                  <SelectItem key={p.code} value={p.code}>{p.code} · {p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {li.category === "hall" && (
                            <Select value={li.plate_type || "veg"} onValueChange={(v) => patchRow(idx, { plate_type: v })}>
                              <SelectTrigger className="w-32 h-8 text-xs" data-testid={`quotation-plate-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="veg">Veg plate</SelectItem>
                                <SelectItem value="non_veg">Non-veg plate</SelectItem>
                                <SelectItem value="package">Package</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-6"><Input placeholder="Description" value={li.description} onChange={(e) => patchRow(idx, { description: e.target.value })} data-testid={`quotation-desc-${idx}`} /></div>
                          <div className="col-span-2"><Input type="number" min="0" placeholder="Qty" value={li.qty} onChange={(e) => patchRow(idx, { qty: e.target.value })} data-testid={`quotation-qty-${idx}`} /></div>
                          <div className="col-span-3"><Input type="number" min="0" placeholder="Rate" value={li.rate} onChange={(e) => patchRow(idx, { rate: e.target.value })} data-testid={`quotation-rate-${idx}`} /></div>
                          <div className="col-span-1"><Button type="button" size="icon" variant="ghost" onClick={() => removeRow(idx)} className="text-destructive"><TrashIcon size={12} /></Button></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Discount (₹)</Label><Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></div>
                    <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  </div>

                  <Card className="p-3 bg-muted/30 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span className="font-mono-plex">{inr(totals.subtotal)}</span></div>
                    <div className="flex justify-between"><span>Discount</span><span className="font-mono-plex">-{inr(totals.discount)}</span></div>
                    <div className="flex justify-between"><span>GST {form.tax_inclusive ? "(inclusive)" : "(added)"}</span><span className="font-mono-plex">{inr(totals.tax)}</span></div>
                    <div className="flex justify-between font-display text-lg mt-2 pt-2 border-t border-border"><span>Total</span><span data-testid="quotation-total">{inr(totals.total)}</span></div>
                  </Card>

                  <DialogFooter><Button type="submit" data-testid="quotation-submit">Save quotation</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Purpose</TableHead>
              <TableHead>Valid till</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`quotation-row-${r.id}`}>
                <TableCell className="font-mono-plex text-xs">{r.number}</TableCell>
                <TableCell>
                  <p className="font-medium">{r.customer_name}</p>
                  <p className="text-[10px] text-muted-foreground">{r.customer_phone}{r.customer_email && ` · ${r.customer_email}`}</p>
                </TableCell>
                <TableCell className="text-xs">{r.purpose || "—"}</TableCell>
                <TableCell className="text-xs font-mono-plex">{r.valid_till || "—"}</TableCell>
                <TableCell className="text-right font-mono-plex">{inr(r.total)}</TableCell>
                <TableCell><Badge variant="outline" className={statusColor[r.status] || statusColor.draft}>{r.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(r.id, r.number)} data-testid={`quotation-pdf-${r.id}`} title="Download branded PDF">
                      <DownloadSimpleIcon size={12} className="mr-1" /> PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openShare(r, "email")} data-testid={`quotation-share-email-${r.id}`} title="Email quotation">
                      <EnvelopeIcon size={12} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openShare(r, "whatsapp")} data-testid={`quotation-share-wa-${r.id}`} title="Share on WhatsApp" className="text-[hsl(140_60%_35%)]">
                      <WhatsappLogoIcon size={12} />
                    </Button>
                    {r.status === "sent" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "accepted")} data-testid={`quotation-accept-${r.id}`}>
                          <CheckCircleIcon size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setStatus(r.id, "rejected")}>Reject</Button>
                      </>
                    )}
                    {r.status === "accepted" && (
                      <Button size="sm" onClick={() => setStatus(r.id, "converted")} data-testid={`quotation-convert-${r.id}`}>
                        <ArrowRightIcon size={12} className="mr-1" /> Converted
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => del(r.id)} className="text-destructive" data-testid={`quotation-del-${r.id}`}><TrashIcon size={12} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No quotations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Share (Email / WhatsApp) dialog */}
      <Dialog open={!!shareTarget} onOpenChange={(v) => !v && setShareTarget(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {shareChannel === "email" ? <EnvelopeIcon size={16} /> : <WhatsappLogoIcon size={16} className="text-[hsl(140_60%_35%)]" />}
              {shareChannel === "email" ? "Email quotation" : "Share via WhatsApp"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Card className="p-3 bg-muted/30 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Quotation</span><span className="font-mono-plex">{shareTarget?.number}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{shareTarget?.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-mono-plex font-semibold">{inr(shareTarget?.total || 0)}</span></div>
            </Card>
            <div>
              <Label className="text-xs">{shareChannel === "email" ? "Recipient email" : "Recipient phone (with country code)"}</Label>
              <Input value={shareTo} onChange={(e) => setShareTo(e.target.value)} placeholder={shareChannel === "email" ? "guest@example.com" : "+91 98xx xxxxxx"} data-testid="quotation-share-to" />
            </div>
            <div>
              <Label className="text-xs">Personal note (optional)</Label>
              <Textarea rows={3} value={shareMsg} onChange={(e) => setShareMsg(e.target.value)} placeholder={`Hi ${shareTarget?.customer_name || "there"}, please find your quotation attached…`} data-testid="quotation-share-msg" />
              <p className="text-[10px] text-muted-foreground mt-1">A branded PDF is attached automatically. Delivery status is logged on the quotation.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareTarget(null)}>Cancel</Button>
            <Button onClick={sendShare} disabled={shareBusy} data-testid="quotation-share-send">
              <PaperPlaneTiltIcon size={14} className="mr-1" /> {shareBusy ? "Sending…" : `Send ${shareChannel === "email" ? "email" : "WhatsApp"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
